import { useGetTreeById } from 'shared/hooks/trees/hooks'
import React, { ReactNode, useEffect } from 'react'
import { Box, Button } from '@mui/material'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  treeAtom,
  summaryMappingAtom,
  treeSummariesAtom,
  curriculaTitlesForSheetAtom,
} from './atoms'
import { ToDo } from './ToDo'
import { Done } from './Done'
import { SheetSelection } from './SheetSelection'
import { GoogleLoginButton } from 'infrastructure/googleDrive'
import { UnMapped } from './UnMapped'
import { useUrlSearchParam } from 'shared/hooks'
import { SheetParamKey } from './types'
import { useToggleState } from 'shared/hooks'
import { Table } from 'shared/ui/Table/Table'
import { yellow } from '@mui/material/colors'
import { useSummaryDataForCurrentSheet } from './hooks'

const sheetLink =
  'https://docs.google.com/spreadsheets/d/13M74BvrwhG247UhwqyxCkR8rajQkGqtuQ7JBMQbLj08/edit?gid=0#gid=0'
export function Summaries({
  treeId,
  versionId,
}: {
  treeId: string
  versionId: string
}) {
  const { tree, resetTree } = useGetTreeById({ treeId, versionId })
  useSetAtom(treeAtom)({ tree, resetTree })
  const [sheetId] = useUrlSearchParam(SheetParamKey)
  const getSheet = useSummaryDataForCurrentSheet()

  useEffect(() => {
    getSheet(sheetId)
  }, [getSheet, sheetId])

  if (!tree) return <Box>Loading...</Box>

  return (
    <Box>
      <Box>
        <Box padding="10px">
          <SummaryToolExplanation />
        </Box>
        <GoogleLoginButton
          ParentComponent={({ children }: { children?: ReactNode }) => {
            return (
              <Box>
                Summaries come from{' '}
                <a href={sheetLink} target="_blank" rel="noopener noreferrer">
                  this Google sheet
                </a>
                , click to provide access {children}
              </Box>
            )
          }}
          LoggedInComponent={SummaryView}
        />
      </Box>
    </Box>
  )
}

function SummaryView() {
  return (
    <Box>
      <Title />
      <SheetSelection />
      <TreeSummaries />
      <EmptySummariesDisplay />
    </Box>
  )
}

function Title() {
  const { tree } = useAtomValue(treeAtom)

  if (!tree) return <Box>Loading...</Box>
  return (
    <Box>
      <strong>{`Summaries: ${tree.title}`}</strong>
    </Box>
  )
}

export function TreeSummaries() {
  const summaries = useAtomValue(treeSummariesAtom)
  const mappings = useAtomValue(summaryMappingAtom)

  if (!summaries || !mappings) return null

  return (
    <Box>
      <Box>{`Summaries: ${summaries.summaryData.length}`}</Box>
      <Box>
        <Box padding="5px">Click these to open details table.</Box>
        <Done />
        <ToDo />
        <UnMapped />
      </Box>
    </Box>
  )
}

function EmptySummariesDisplay() {
  const { tree } = useAtomValue(treeAtom)
  const titles = useAtomValue(curriculaTitlesForSheetAtom)

  if (!tree || !titles.length || titles.includes(tree.title)) return null

  return (
    <Box>
      <Box>
        This sheet does not contain this tree, to add summaries select the sheet
        with this tree.
      </Box>
      <Box>
        Looking For: <strong>{tree.title}</strong>
      </Box>
      <Box>
        Found:{' '}
        <Box padding="10px">
          {titles.map((title) => (
            <Box key={title}>{title}</Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

const sheetHeaders = {
  curriculum_title:
    'The only use of this is when a tree is not found in a selected sheet we present the titles found in the selected sheet.',
  summary_level:
    'Informs the tool the node type at which to add the summary, for example "unit" if adding to a unit type node',
  [`[NodeType]_title`]: `This is how we find the node to add the summary to. We take all the values of the columns that end in _title and create a type to title map that enables us to find the node. If we're adding a unit summary we need the unit_title value, if we're adding a section summary we need unit_title and section_title values, and so on.`,
  tree_id:
    'Paired with version is how we identify the tree where the summaries should be added.',
  version:
    'Paired with tree_id is how we identify the tree where the summaries should be added.',
  summary:
    'This value defines if it is a long or short summary. If the value includes "315" then it is a long summary, otherwise it is a short summary type.',
  generations: 'This is the summary content.',
  id: 'Not Used',
  grade: 'Not Used',
}

function SummaryToolExplanation() {
  const [show, toggleShow] = useToggleState(false)

  if (!show) {
    return (
      <Box>
        <Button onClick={() => toggleShow()}>Explain This Tool</Button>
      </Box>
    )
  }

  const columns = [
    {
      title: 'Header',
      rowContent([header]: [string, string]) {
        return (
          <Box>
            <strong>{header}</strong>
          </Box>
        )
      },
      width: '15%',
    },
    {
      title: 'Function',
      rowContent([, desc]: [string, string]) {
        return <Box>{desc}</Box>
      },
      width: '85%',
    },
  ]
  return (
    <Box>
      <Button onClick={() => toggleShow()}>Hide Explanation</Button>
      <Box
        borderRadius="10px"
        border="1px solid yellow"
        style={{ backgroundColor: yellow[50] }}
        padding="25px"
      >
        <Box>
          The sheet that drives this tool is found{' '}
          <a href={sheetLink} target="_blank" rel="noopener noreferrer">
            here
          </a>
        </Box>
        <Box padding="10px" borderRadius="10px">
          <Table
            overrideHeight={380}
            columns={columns}
            items={Object.entries(sheetHeaders)}
          />
        </Box>
      </Box>
    </Box>
  )
}
